import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { FHButton, FHCounter, FHTimeTag } from '../../../../base';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_GRAY5, FORTA_GRAY2 } from '../../../../../utils/colors';
import { TPGoalDataCollectionMethod } from '../../../treatment-plan/goals/common/constants/general';
import DeleteIcon from '../../../../../assets/images/trash-new.svg';
import TimeTagStartIcon from '../../../../../assets/images/timetag-start.svg';
import TimeTagEndIcon from '../../../../../assets/images/timetag-end.svg';
import ParentSNDurationTargetRow from './ParentSNDurationTargetRow';

import type { RootState } from '../../../../../store';
import type { ParentGoalTarget } from './ParentTargetProgress';
import type { Moment } from 'moment';

interface TargetRowProps {
  index: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  isEditable: boolean;
  dataLoading: boolean;
  target: ParentGoalTarget;
  dataCollectionMethod: string;
  isSupervisionCompleted: boolean;
  onDelete: (evaluationId: number) => void;
  onUpdate: (data: { [key: string]: number }, evaluationId: number) => void;
  onUpdateRateEvent: (data: any, targetEvaluationId: number, rateEventId: number | null) => void;
  onDeleteRateEvent: (targetEvaluationId: number, rateEventId: number) => void;
}

function TargetProgressRow({
  index,
  isEditable,
  isFirstRow,
  isLastRow,
  target,
  dataLoading,
  dataCollectionMethod,
  isSupervisionCompleted,
  onDelete,
  onUpdate,
  onUpdateRateEvent,
  onDeleteRateEvent,
}: TargetRowProps) {
  const { t } = useTranslation('sessionNotes');
  const { current: parentSupervision, loading } = useSelector(
    (state: RootState) => state.parentSupervision,
  );
  const [isCounterFocused, setIsCounterFocused] = useState<boolean>(false);
  const [rateEvents, setRateEvents] = useState<any[]>([]);

  useEffect(() => {
    setRateEvents(target?.rate_events || []);
  }, [target]);

  const targetId = target.id;

  const handleAddTimeTag = useCallback(async () => {
    const newTime = moment();
    const startTime = moment(parentSupervision?.date_start);
    const endTime = moment(parentSupervision?.date_start).add(
      parentSupervision?.expected_duration,
      'seconds',
    );

    let eventTime: Moment | null = newTime;
    let isOutsideRange = false;

    if (newTime.isSameOrBefore(startTime) || newTime.isSameOrAfter(endTime)) {
      eventTime = null;
      isOutsideRange = true;
    } else {
      eventTime = newTime.milliseconds(0);
    }

    const newRateEvent = {
      id: null,
      date_of_event: eventTime ? eventTime.toISOString() : null,
    };

    // Store the previous state before making any changes
    const previousEvents = [...rateEvents];

    try {
      setRateEvents((prev) => [...prev, newRateEvent]);
      if (!isOutsideRange && targetId !== undefined) {
        await onUpdateRateEvent(newRateEvent, targetId, null);
      }
    } catch (error: any) {
      // Restore the previous state completely
      setRateEvents(previousEvents);

      // Show error toast to user
      toast.error(error?.message || 'Failed to add time tag. Please try again.');
    }
  }, [parentSupervision, target?.id, onUpdateRateEvent, rateEvents]);

  const handleUpdateRateEvent = useCallback(
    async (newTime: Moment, rateEventId: number) => {
      if (targetId === undefined) {
        return;
      }

      let originalEvent: any = null;
      setRateEvents((prev) => {
        originalEvent = prev.find((event) => event.id === rateEventId);
        const updatedEvent = { ...originalEvent, date_of_event: newTime.toISOString() };
        return prev.map((event) => (event.id === rateEventId ? updatedEvent : event));
      });

      try {
        const updatedEvent = { date_of_event: newTime.toISOString() };
        await onUpdateRateEvent(updatedEvent, targetId, rateEventId);
      } catch (error: any) {
        // Revert to the original event on failure
        if (originalEvent) {
          setRateEvents((prev) =>
            prev.map((event) => (event.id === rateEventId ? { ...originalEvent } : event)),
          );
        }
        console.error(error?.message || 'Failed to update time tag. Please try again.');
      }
    },
    [target?.id, onUpdateRateEvent],
  );

  const handleDeleteRateEvent = useCallback(
    (rateEventId: number) => {
      if (targetId !== undefined) {
        onDeleteRateEvent(targetId, rateEventId);
      }
      setRateEvents((prev) => prev.filter((event) => event.id !== rateEventId));
    },
    [target?.id, onDeleteRateEvent],
  );

  return (
    <Box
      sx={{
        borderBottom: isLastRow ? 'none' : `2px solid ${FORTA_BLUE.light3}`,
        pb: isLastRow ? 0 : 2.7,
        pt: isFirstRow ? 0 : 2,
      }}
    >
      <Grid container>
        {dataCollectionMethod !== TPGoalDataCollectionMethod.RATE ? (
          <Grid xs={12} sm={7} item>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {!isSupervisionCompleted && (
                <IconButton
                  data-testid={`delete-target-${target.target_label}`}
                  sx={{
                    p: 1,
                    mr: 1,
                    opacity: !isEditable || isCounterFocused ? 0.5 : 1,
                  }}
                  disabled={!isEditable || isCounterFocused}
                  onClick={() => {
                    if (targetId !== undefined) {
                      onDelete(targetId);
                    }
                  }}
                >
                  <img src={DeleteIcon} alt="Delete" />
                </IconButton>
              )}
              <Typography
                sx={{
                  borderLeft: isSupervisionCompleted ? 'none' : `1px solid ${FORTA_BLUE.primary}`,
                  color: FORTA_GRAY4,
                  pl: 2,
                }}
              >
                {target?.target_label}
              </Typography>
            </Box>
          </Grid>
        ) : (
          <Grid xs={12} sm={7} item>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                mt: 1,
                mb: 2,
              }}
            >
              {!isSupervisionCompleted && (
                <IconButton
                  data-testid={`delete-target-${target.target_label}`}
                  sx={{
                    p: 1,
                    mr: 1,
                    mb: 1,
                    opacity: !isEditable || isCounterFocused ? 0.5 : 1,
                  }}
                  disabled={!isEditable || isCounterFocused}
                  onClick={() => {
                    if (targetId !== undefined) {
                      onDelete(targetId);
                    }
                  }}
                >
                  <img src={DeleteIcon} alt="Delete" />
                </IconButton>
              )}
              <Typography
                sx={{
                  fontSize: '16px',
                  color: FORTA_GRAY4,
                  textAlign: 'left',
                  mb: 1,
                  borderLeft: isSupervisionCompleted ? 'none' : `1px solid ${FORTA_BLUE.primary}`,
                  pl: 2,
                }}
              >
                {index + 1}. {target?.target_label}
              </Typography>
            </Box>
          </Grid>
        )}

        {dataCollectionMethod === TPGoalDataCollectionMethod.RATE ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              border: `1px solid ${FORTA_GRAY2}`,
              borderRadius: '8px',
              padding: '8px 24px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
              <Typography sx={{ fontSize: '16px', color: FORTA_GRAY4, textAlign: 'left' }}>
                <b>Rate tracker</b> : {rateEvents.length} total events
              </Typography>
            </Box>
            <Grid
              xs={12}
              sm={12}
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {/* Static start time box */}
              <Box
                sx={{
                  width: 222,
                  minHeight: 60,
                  display: 'flex',
                  alignItems: 'center',
                  my: 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: FORTA_BLUE.light3,
                    width: '100%',
                    borderRadius: '4px',
                    py: 1,
                    px: 1,
                    gap: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'not-allowed',
                  }}
                >
                  <img src={TimeTagStartIcon} alt="start-icon" width={21} height={17} />
                  <Typography color={FORTA_GRAY5} fontSize={16}>
                    {moment(parentSupervision?.date_start).format('hh : mm : ss A')}
                  </Typography>
                </Box>
              </Box>
              {/* Dynamic time tags */}
              {rateEvents.map((tag) => (
                <Box
                  key={tag.id || null}
                  sx={{
                    width: 222,
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FHTimeTag
                    id={tag.id || 0}
                    time={tag.date_of_event ? moment(tag.date_of_event) : null}
                    maxTime={
                      parentSupervision?.date_start && parentSupervision?.expected_duration
                        ? moment(parentSupervision.date_start).add(
                            parentSupervision.expected_duration,
                            'seconds',
                          )
                        : moment()
                    }
                    minTime={moment(parentSupervision?.date_start)}
                    debounceTime={700}
                    disabled={isSupervisionCompleted || !isEditable}
                    onDelete={handleDeleteRateEvent}
                    onUpdate={(newTime) => handleUpdateRateEvent(newTime, tag.id || 0)}
                    containerStyles={{ width: '100%' }}
                  />
                </Box>
              ))}
              {/* Static end time box */}
              <Box
                sx={{
                  width: 222,
                  minHeight: 80,
                  display: 'flex',
                  alignItems: 'center',
                  my: 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: FORTA_BLUE.light3,
                    width: '100%',
                    borderRadius: '4px',
                    py: 1,
                    px: 1,
                    gap: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'not-allowed',
                  }}
                >
                  <Typography color={FORTA_GRAY5} fontSize={16}>
                    {parentSupervision?.date_start && parentSupervision?.expected_duration
                      ? moment(parentSupervision.date_start)
                          .add(parentSupervision.expected_duration, 'seconds')
                          .format('hh : mm : ss A')
                      : moment().format('hh : mm : ss A')}
                  </Typography>
                  <img src={TimeTagEndIcon} alt="end-icon" width={21} height={17} />
                </Box>
              </Box>
            </Grid>
            {!isSupervisionCompleted && (
              <Grid
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  mt: 2,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Typography sx={{ fontSize: '16px', color: FORTA_GRAY4 }}>
                  {t('goalsPracticed.toAddAnEvent')}
                </Typography>
                <FHButton
                  startIcon={<AddCircleOutlineOutlined />}
                  disabled={
                    rateEvents.some((event) => event.date_of_event === null) ||
                    !isEditable ||
                    loading
                  }
                  onClick={handleAddTimeTag}
                >
                  {t('goalsPracticed.addEvent')}
                </FHButton>
              </Grid>
            )}
          </Box>
        ) : dataCollectionMethod === TPGoalDataCollectionMethod.DURATION && targetId !== undefined ? (
          <ParentSNDurationTargetRow
            dataLoading={dataLoading}
            trailId={targetId}
            durationEvents={target?.duration_events ?? []}
          />
        ) : dataCollectionMethod === TPGoalDataCollectionMethod.INSTANCES ? (
          <Grid xs={12} sm={5} item>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: '2.1rem',
                mt: -2,
                width: '100%',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    mb: 1,
                  }}
                >
                  Instances
                </Typography>
                <FHCounter
                  disabled={!isEditable}
                  delay={500}
                  count={target.occurrence}
                  onFocus={setIsCounterFocused}
                  dataTestId={`InstanceTarget${target.target_label}`}
                  onCountChange={(newCount) => {
                    const data = {
                      occurrence: newCount,
                    };
                    if (targetId !== undefined) {
                      onUpdate(data, targetId);
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>
        ) : (
          <Grid xs={12} sm={5} item>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: '2.1rem',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    mb: 1,
                  }}
                >
                  Unsuccessful
                </Typography>
                <FHCounter
                  disabled={!isEditable}
                  delay={500}
                  count={target.unsuccessful_attempts}
                  onFocus={setIsCounterFocused}
                  dataTestId={`UnsuccessfulTarget${target.target_label}`}
                  onCountChange={(newCount) => {
                    const data = {
                      successful_attempts: target?.successful_attempts,
                      unsuccessful_attempts: newCount,
                    };
                    if (targetId !== undefined) {
                      onUpdate(data, targetId);
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    mb: 1,
                  }}
                >
                  Successful
                </Typography>
                <FHCounter
                  delay={500}
                  disabled={!isEditable}
                  count={target.successful_attempts}
                  onFocus={setIsCounterFocused}
                  dataTestId={`SuccessfulTarget${target.target_label}`}
                  onCountChange={(newCount) => {
                    const data = {
                      successful_attempts: newCount,
                      unsuccessful_attempts: target.unsuccessful_attempts,
                    };
                    if (targetId !== undefined) {
                      onUpdate(data, targetId);
                    }
                  }}
                />
              </Box>
            </Box>
            <Typography sx={{ textAlign: 'right', pr: 16, mt: 2 }}>
              {`Total attempts: ${target?.total_attempts}`}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default TargetProgressRow;
